import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Scale, Clock, CreditCard, Globe, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TermsOfService = () => {
  return (
    <>
      <Navbar />
      <div className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link to="/" className="inline-flex items-center text-ta-green hover:underline">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center mb-6">
              <FileText className="h-8 w-8 text-ta-green mr-3" />
              <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
            </div>
            
            <div className="text-sm text-gray-500 mb-8">
              <p>Last Updated: June 15, 2025</p>
            </div>
            
            <div className="prose max-w-none text-gray-700">
              <p className="lead text-lg mb-6">
                Welcome to TravelCord. These Terms of Service ("Terms") govern your use of the TravelCord website, mobile applications, and services (collectively, the "Service").
              </p>
              
              <div className="bg-ta-green/5 p-4 rounded-lg mb-8 border-l-4 border-ta-green">
                <p className="font-medium">
                  Please read these Terms carefully before using our Service. By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the Terms, you may not access the Service.
                </p>
              </div>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <Globe className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">1. Service Description</h2>
                </div>
                
                <p className="mb-4">
                  TravelCord is a platform that connects travelers with local guides in Uganda. Our Service allows travelers to discover, book, and experience tours and activities led by verified local guides. Guides can create profiles, list their services, and connect with potential clients.
                </p>
                
                <p className="mb-4">
                  TravelCord acts as an intermediary between travelers and guides. We facilitate the connection and booking process but are not responsible for the actual delivery of tour services. The contract for tour services is between the traveler and the guide.
                </p>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <FileText className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">2. User Accounts</h2>
                </div>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">2.1 Account Creation</h3>
                <p className="mb-4">
                  To use certain features of our Service, you must register for an account. When you register, you agree to provide accurate, current, and complete information about yourself. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">2.2 Account Types</h3>
                <p className="mb-4">
                  TravelCord offers two types of user accounts:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Traveler Accounts:</strong> For individuals seeking to discover and book experiences with local guides.</li>
                  <li><strong>Guide Accounts:</strong> For verified local guides who wish to offer their services to travelers.</li>
                </ul>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">2.3 Guide Verification</h3>
                <p className="mb-4">
                  To become a verified guide on TravelCord, individuals must complete our application process, which includes providing personal information, professional qualifications, and supporting documentation. TravelCord reserves the right to approve or reject guide applications at our discretion.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">2.4 Account Termination</h3>
                <p className="mb-4">
                  TravelCord reserves the right to suspend or terminate your account at any time for any reason, including but not limited to violation of these Terms. You may also terminate your account at any time by contacting us at support@travelcord.guide.
                </p>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <Scale className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">3. User Responsibilities</h2>
                </div>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">3.1 General Conduct</h3>
                <p className="mb-4">
                  When using our Service, you agree to:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Respect the rights of other users</li>
                  <li>Provide accurate and truthful information</li>
                  <li>Use the Service only for its intended purposes</li>
                  <li>Not engage in any activity that could harm, disable, or impair the Service</li>
                </ul>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">3.2 Prohibited Activities</h3>
                <p className="mb-4">
                  You agree not to:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Use the Service for any illegal purpose</li>
                  <li>Impersonate any person or entity</li>
                  <li>Harass, abuse, or harm another person</li>
                  <li>Send spam or other unsolicited messages</li>
                  <li>Attempt to access accounts or data that don't belong to you</li>
                  <li>Post or transmit malicious code or viruses</li>
                  <li>Interfere with or disrupt the Service</li>
                  <li>Collect user information without consent</li>
                </ul>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">3.3 Guide Responsibilities</h3>
                <p className="mb-4">
                  If you are a guide, you additionally agree to:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Provide accurate information about your qualifications, experience, and services</li>
                  <li>Maintain all necessary licenses, permits, and insurance required for your activities</li>
                  <li>Deliver services as described in your listings</li>
                  <li>Treat travelers with respect and professionalism</li>
                  <li>Comply with all safety standards and regulations</li>
                  <li>Communicate promptly and clearly with travelers</li>
                  <li>Honor all confirmed bookings</li>
                </ul>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">3.4 Traveler Responsibilities</h3>
                <p className="mb-4">
                  If you are a traveler, you additionally agree to:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Provide accurate information when making bookings</li>
                  <li>Respect the customs, rules, and guidelines presented by guides</li>
                  <li>Arrive on time for scheduled activities</li>
                  <li>Pay all agreed-upon fees</li>
                  <li>Communicate any special needs or requirements in advance</li>
                  <li>Provide honest and fair reviews of your experiences</li>
                </ul>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <CreditCard className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">4. Bookings and Payments</h2>
                </div>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">4.1 Booking Process</h3>
                <p className="mb-4">
                  Travelers can book experiences directly through the TravelCord platform. A booking is confirmed once payment has been processed and both the traveler and guide have received confirmation.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">4.2 Pricing and Fees</h3>
                <p className="mb-4">
                  All prices listed on TravelCord are in US Dollars (USD) unless otherwise specified. Guides set their own prices for their services. TravelCord charges a service fee on each booking, which is added to the guide's price and displayed to travelers before checkout.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">4.3 Payment Processing</h3>
                <p className="mb-4">
                  Payments are processed securely through our third-party payment processors. By making a payment, you authorize us to charge the amount specified to your selected payment method.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">4.4 Guide Payouts</h3>
                <p className="mb-4">
                  Guides receive payment for their services after the experience has been completed, minus TravelCord's service fee. Payouts are typically processed within 5-7 business days.
                </p>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <Clock className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">5. Cancellations and Refunds</h2>
                </div>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">5.1 Cancellation Policies</h3>
                <p className="mb-4">
                  Cancellation policies vary by experience and are set by guides. The specific cancellation policy for each experience is displayed on the experience listing page before booking. Generally, cancellations fall into these categories:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Flexible:</strong> Full refund if canceled at least 24 hours before the start time.</li>
                  <li><strong>Moderate:</strong> Full refund if canceled at least 3 days before the start time.</li>
                  <li><strong>Strict:</strong> Full refund if canceled at least 7 days before the start time.</li>
                  <li><strong>Non-refundable:</strong> No refunds available.</li>
                </ul>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">5.2 Guide Cancellations</h3>
                <p className="mb-4">
                  If a guide cancels a confirmed booking, the traveler will receive a full refund. Guides who frequently cancel bookings may have their accounts suspended or terminated.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">5.3 Extenuating Circumstances</h3>
                <p className="mb-4">
                  In cases of extenuating circumstances (such as natural disasters, serious illness, or government travel restrictions), TravelCord may override the standard cancellation policy and issue refunds at our discretion.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">5.4 Refund Processing</h3>
                <p className="mb-4">
                  Refunds are processed to the original payment method used for the booking. Processing times vary depending on the payment method and financial institution, but typically take 5-10 business days.
                </p>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">6. Disclaimers and Limitations of Liability</h2>
                </div>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">6.1 Service Provided "As Is"</h3>
                <p className="mb-4">
                  The Service is provided on an "as is" and "as available" basis. TravelCord makes no representations or warranties of any kind, express or implied, regarding the operation of the Service or the information, content, materials, or products included on the Service.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">6.2 No Guarantee of Guide Services</h3>
                <p className="mb-4">
                  While TravelCord takes reasonable steps to verify guides, we do not guarantee the quality, safety, suitability, or legality of any guide or experience. Travelers participate in experiences at their own risk.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">6.3 Limitation of Liability</h3>
                <p className="mb-4">
                  To the maximum extent permitted by law, TravelCord shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">6.4 Indemnification</h3>
                <p className="mb-4">
                  You agree to indemnify and hold harmless TravelCord from any claims arising from your use of the Service or violation of these Terms.
                </p>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <Shield className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">7. Intellectual Property</h2>
                </div>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">7.1 TravelCord Content</h3>
                <p className="mb-4">
                  The Service and its original content are owned by TravelCord and protected by international copyright, trademark, and other intellectual property laws.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">7.2 User Content</h3>
                <p className="mb-4">
                  By posting content on the Service, you grant TravelCord a non-exclusive, worldwide license to use, modify, and distribute that content.
                </p>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <Globe className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">8. Nightlife Services</h2>
                </div>
                
                <p className="mb-4">
                  TravelCord offers nightlife packages and services in select locations. By booking these services, you additionally agree to:
                </p>
                
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Comply with all local laws regarding alcohol consumption and nightlife activities</li>
                  <li>Follow the guidance of your designated nightlife guide at all times</li>
                  <li>Respect the dress codes and entry requirements of all venues</li>
                  <li>Behave responsibly and respectfully toward staff and other patrons</li>
                  <li>Not engage in any illegal activities during the nightlife experience</li>
                </ul>
                
                <p className="mb-4">
                  TravelCord reserves the right to terminate any nightlife experience if a participant is behaving in a manner that is disruptive, dangerous, or illegal, with no refund provided.
                </p>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <Globe className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">9. Self-Drive Safaris</h2>
                </div>
                
                <p className="mb-4">
                  For self-drive safari services, the following additional terms apply:
                </p>
                
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Drivers must be at least 23 years old with a valid driver's license</li>
                  <li>Drivers must have at least 2 years of driving experience</li>
                  <li>A security deposit is required for all vehicle rentals</li>
                  <li>Vehicles must only be used on designated roads and in accordance with local traffic laws</li>
                  <li>Travelers are responsible for any damage to the vehicle not covered by insurance</li>
                  <li>TravelCord is not liable for any accidents, injuries, or damages that occur during self-drive safaris</li>
                </ul>
                
                <p className="mb-4">
                  Detailed terms for self-drive safaris will be provided at the time of booking.
                </p>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <Globe className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">10. Changes to Terms</h2>
                </div>
                
                <p className="mb-4">
                  We reserve the right to modify these Terms at any time. We will notify you of any material changes 30 days before they take effect.
                </p>
              </section>
              
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Us</h2>
                
                <p className="mb-4">
                  If you have any questions about these Terms, please contact us at:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium">TravelCord</p>
                  <p>Ntinda, Kampala, Uganda</p>
                  <p>Email: info@travelcord.guide</p>
                  <p>Phone: +256766830194</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TermsOfService;